/**
 * Admin → Board & Committee tab.
 * Three stacked sections:
 *   1. Roster — add / edit / soft-delete board + committee members.
 *   2. Meetings — schedule Jitsi meetings, log past meetings, minutes + attendance, Minutes Book PDF.
 *   3. Broadcasts — composer + history with per-recipient read receipts.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import {
  BookOpen,
  Calendar,
  CheckCircle2,
  Copy,
  ExternalLink,
  Mail,
  NotebookPen,
  Pencil,
  Plus,
  Send,
  Shield,
  Trash2,
  Users,
  Video,
  X,
} from "lucide-react";
import { Field, FORMAT_LABELS, Overlay, downloadPdf, formatIso, inputCls } from "./boardShared";
import { LogMeetingModal, MinutesModal } from "./BoardMinutes";

export default function BoardTab() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadMembers = useCallback(async () => {
    try {
      const r = await api.get("/admin/board/members");
      setMembers(r.data.members || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  return (
    <div className="space-y-10" data-testid="admin-board-tab">
      <RosterSection members={members} loading={loading} load={loadMembers} />
      <MeetingsSection members={members} />
      <BroadcastsSection members={members} />
    </div>
  );
}

// ===========================================================
// Section 1 — Roster
// ===========================================================

function RosterSection({ members, loading, load }) {
  const [editing, setEditing] = useState(null); // null | "new" | member doc

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the roster?`)) return;
    try {
      await api.delete(`/admin/board/members/${m.id}`);
      toast.success(`${m.name} removed`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <section data-testid="admin-board-roster">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <Users size={12} className="text-bdu-gold" /> Section 1 · Roster
          </div>
          <h3 className="mt-2 font-serif text-2xl text-bdu-cream">Board & Organizing Committee</h3>
          <p className="mt-1 text-sm text-bdu-cream/55 max-w-2xl">
            People shown here with "Public" on appear on <code>/board</code>. Email + phone stay internal — the public endpoint never returns them.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing("new")}
          className="btn-gold text-xs inline-flex items-center gap-2"
          data-testid="admin-board-add-member"
        >
          <Plus size={14} /> Add Member
        </button>
      </div>

      <div className="border border-bdu-gold/20 overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-board-member-table">
          <thead className="bg-bdu-surface2">
            <tr className="text-[10px] tracking-[0.22em] uppercase text-bdu-cream/55">
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Group</th>
              <th className="text-left px-4 py-3">County</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-center px-4 py-3">Public</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-6 text-bdu-cream/50 text-xs">Loading…</td></tr>
            )}
            {!loading && members.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-bdu-cream/50 text-xs italic">
                No members yet. Add your first board member to seed <code>/board</code>.
              </td></tr>
            )}
            {members.map((m) => (
              <tr
                key={m.id}
                className="border-t border-bdu-gold/10 hover:bg-bdu-gold/5"
                data-testid={`admin-board-row-${m.id}`}
              >
                <td className="px-4 py-3 text-bdu-cream">{m.name}</td>
                <td className="px-4 py-3 text-bdu-cream/75">{m.title}</td>
                <td className="px-4 py-3">
                  <span className="text-[10px] tracking-[0.2em] uppercase text-bdu-gold border border-bdu-gold/40 px-2 py-1">
                    {m.group === "board" ? "Board" : "Committee"}
                  </span>
                </td>
                <td className="px-4 py-3 text-bdu-cream/60">{m.county || "—"}</td>
                <td className="px-4 py-3 text-bdu-cream/60">{m.email || "—"}</td>
                <td className="px-4 py-3 text-center">
                  {m.is_public ? (
                    <CheckCircle2 size={14} className="text-emerald-400 inline" />
                  ) : (
                    <span className="text-bdu-cream/40 text-xs">hidden</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                  <button
                    onClick={() => setEditing(m)}
                    className="text-bdu-cream/70 hover:text-bdu-gold"
                    data-testid={`admin-board-edit-${m.id}`}
                    title="Edit"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => remove(m)}
                    className="text-bdu-cream/70 hover:text-red-400"
                    data-testid={`admin-board-delete-${m.id}`}
                    title="Remove"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <MemberModal
          member={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </section>
  );
}

function MemberModal({ member, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    group: member?.group || "board",
    name: member?.name || "",
    title: member?.title || "",
    bio: member?.bio || "",
    photo_url: member?.photo_url || "",
    county: member?.county || "",
    city: member?.city || "",
    email: member?.email || "",
    phone: member?.phone || "",
    linkedin_url: member?.linkedin_url || "",
    display_order: member?.display_order ?? 999,
    is_public: member ? !!member.is_public : true,
  }));
  const [busy, setBusy] = useState(false);
  const isEdit = !!member;

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        ...form,
        email: form.email.trim() || null,
        display_order: Number(form.display_order) || 999,
      };
      if (isEdit) {
        await api.patch(`/admin/board/members/${member.id}`, body);
        toast.success(`${form.name} updated`);
      } else {
        await api.post("/admin/board/members", body);
        toast.success(`${form.name} added`);
      }
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose} testId="admin-board-member-modal">
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="relative max-w-2xl w-full bg-bdu-bg border border-bdu-gold/40 p-6 sm:p-8"
        data-testid="admin-board-member-form"
      >
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-bdu-cream/60 hover:text-bdu-cream" aria-label="Close">
          <X size={18} />
        </button>
        <div className="label-eyebrow flex items-center gap-2"><Users size={12} className="text-bdu-gold" /> Roster</div>
        <h3 className="mt-3 font-serif text-2xl text-bdu-cream">
          {isEdit ? `Edit · ${member.name}` : "Add new member"}
        </h3>

        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <Field label="Group *">
            <select value={form.group} onChange={upd("group")} className={inputCls} data-testid="admin-board-field-group">
              <option value="board">Board of Directors</option>
              <option value="committee">Organizing Committee</option>
            </select>
          </Field>
          <Field label="Display order">
            <input type="number" value={form.display_order} onChange={upd("display_order")} className={inputCls} />
          </Field>
          <Field label="Full name *">
            <input required value={form.name} onChange={upd("name")} className={inputCls} data-testid="admin-board-field-name" />
          </Field>
          <Field label="Title / role *">
            <input required value={form.title} onChange={upd("title")} className={inputCls} placeholder="Chair · Treasurer · Director"
                    data-testid="admin-board-field-title" />
          </Field>
          <Field label="County">
            <select value={form.county} onChange={upd("county")} className={inputCls}>
              <option value="">—</option>
              <option value="Fulton">Fulton</option>
              <option value="DeKalb">DeKalb</option>
              <option value="Other">Other</option>
            </select>
          </Field>
          <Field label="City">
            <input value={form.city} onChange={upd("city")} className={inputCls} placeholder="Atlanta, GA" />
          </Field>
          <Field label="Email (private)">
            <input type="email" value={form.email} onChange={upd("email")} className={inputCls} data-testid="admin-board-field-email" />
          </Field>
          <Field label="Phone (private)">
            <input value={form.phone} onChange={upd("phone")} className={inputCls} />
          </Field>
          <Field label="Photo URL">
            <input value={form.photo_url} onChange={upd("photo_url")} className={inputCls} placeholder="https://…" />
          </Field>
          <Field label="LinkedIn URL">
            <input value={form.linkedin_url} onChange={upd("linkedin_url")} className={inputCls} placeholder="https://linkedin.com/in/…" />
          </Field>
        </div>
        <Field label="Bio" className="mt-4">
          <textarea rows={4} value={form.bio} onChange={upd("bio")} className={`${inputCls} resize-none`} maxLength={2000}
                     placeholder="One or two sentences describing experience and why they are serving." />
        </Field>
        <label className="mt-4 flex items-center gap-2 text-sm text-bdu-cream/70 cursor-pointer" data-testid="admin-board-field-public">
          <input type="checkbox" checked={form.is_public} onChange={(e) => setForm((f) => ({ ...f, is_public: e.target.checked }))} />
          Show on the public /board page
        </label>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn-ghost text-xs">Cancel</button>
          <button type="submit" disabled={busy} className="btn-gold text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                   data-testid="admin-board-member-submit">
            {busy ? "Saving…" : isEdit ? "Save changes" : "Add member"}
          </button>
        </div>
      </form>
    </Overlay>
  );
}

// ===========================================================
// Section 2 — Meetings
// ===========================================================

function MinutesButton({ m, onClick }) {
  const st = m.minutes?.status;
  const label = st === "approved" ? "Minutes · Approved" : st === "draft" ? "Minutes · Draft" : "Record minutes";
  const cls = st === "approved"
    ? "border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10"
    : "border-bdu-gold/40 text-bdu-gold hover:bg-bdu-gold/10";
  return (
    <button onClick={onClick} className={`text-xs tracking-[0.2em] uppercase border px-3 py-2 inline-flex items-center gap-1.5 ${cls}`}
            data-testid={`admin-board-meeting-${m._id}-minutes`}>
      {st === "approved" ? <CheckCircle2 size={12} /> : <NotebookPen size={12} />} {label}
    </button>
  );
}

function MeetingCard({ m, isPast, resending, onResend, onCancel, onMinutes }) {
  const approved = m.minutes?.status === "approved";
  return (
    <div className="border border-bdu-gold/20 bg-bdu-surface2 p-5" data-testid={`admin-board-meeting-${m._id}`}>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] tracking-[0.22em] uppercase text-bdu-gold">
            {formatIso(m.starts_at)} · {m.duration_minutes} min{m.logged ? " · Logged" : ""}
          </div>
          <div className="font-serif text-lg text-bdu-cream mt-1">{m.title}</div>
          {m.description && <p className="mt-2 text-xs text-bdu-cream/60 leading-relaxed">{m.description}</p>}
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-bdu-cream/55">
            {m.jitsi_url ? (
              <>
                <a href={m.jitsi_url} target="_blank" rel="noopener noreferrer"
                   className="text-bdu-gold hover:underline inline-flex items-center gap-1"
                   data-testid={`admin-board-meeting-${m._id}-join`}>
                  <Video size={12} /> {m.jitsi_url}
                </a>
                <button type="button" onClick={() => { navigator.clipboard.writeText(m.jitsi_url); toast.success("Link copied"); }}
                        className="text-bdu-cream/50 hover:text-bdu-gold" title="Copy link">
                  <Copy size={12} />
                </button>
                {m.password && <span className="text-bdu-cream/50">· passcode <code className="text-bdu-cream">{m.password}</code></span>}
              </>
            ) : (
              <span>{FORMAT_LABELS[m.meeting_format] || "Offline"}{m.location ? ` — ${m.location}` : ""}</span>
            )}
            <span className="text-bdu-cream/40">· {m.attendees?.length || 0} invited</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <MinutesButton m={m} onClick={onMinutes} />
          {!isPast && m.jitsi_url && (
            <button onClick={onResend} disabled={resending}
                    className="text-xs tracking-[0.2em] uppercase border border-bdu-gold/40 hover:bg-bdu-gold/10 px-3 py-2 text-bdu-cream disabled:opacity-40"
                    data-testid={`admin-board-meeting-${m._id}-resend`}>
              {resending ? "Sending…" : "Resend invites"}
            </button>
          )}
          {!approved && (
            <button onClick={onCancel}
                    className="text-xs tracking-[0.2em] uppercase border border-red-400/30 hover:bg-red-400/10 px-3 py-2 text-red-300"
                    data-testid={`admin-board-meeting-${m._id}-cancel`}>
              {isPast ? "Remove" : "Cancel"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function MeetingsSection({ members }) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("upcoming");
  const [modal, setModal] = useState(null); // "create" | "log" | meetingId string
  const [resending, setResending] = useState(null);

  const load = useCallback(async () => {
    try {
      const m = await api.get("/admin/board/meetings", { params: { include_past: true } });
      setMeetings(m.data.meetings || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const now = Date.now();
  const upcoming = meetings.filter((m) => new Date(m.starts_at).getTime() >= now).reverse();
  const past = meetings.filter((m) => new Date(m.starts_at).getTime() < now);
  const shown = view === "upcoming" ? upcoming : past;
  const approvedCount = meetings.filter((m) => m.minutes?.status === "approved").length;

  const resend = async (meeting) => {
    setResending(meeting._id);
    try {
      const r = await api.post(`/admin/board/meetings/${meeting._id}/resend`);
      toast.success(`Invites resent · ${r.data.sent}/${r.data.recipients}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setResending(null); }
  };

  const cancel = async (meeting) => {
    const msg = meeting.jitsi_url && new Date(meeting.starts_at).getTime() >= now
      ? `Cancel "${meeting.title}"? Invitees won't be notified automatically.`
      : `Remove "${meeting.title}" and any draft minutes?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/board/meetings/${meeting._id}`);
      toast.success("Meeting removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const tabCls = (k) => `text-[11px] tracking-[0.2em] uppercase px-4 py-2 border -ml-px ${view === k ? "bg-bdu-gold text-bdu-bg border-bdu-gold" : "border-bdu-gold/25 text-bdu-cream/60 hover:text-bdu-cream"}`;
  const noMembers = members.length === 0;

  return (
    <section data-testid="admin-board-meetings">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <Video size={12} className="text-bdu-gold" /> Section 2 · Meetings &amp; Minutes
          </div>
          <h3 className="mt-2 font-serif text-2xl text-bdu-cream">Meetings, attendance &amp; minutes</h3>
          <p className="mt-1 text-sm text-bdu-cream/55 max-w-2xl">
            Schedule Jitsi meetings with emailed invites, or log meetings held in person or by phone. Record attendance,
            quorum, motions and votes, then approve to lock and sign. Approved minutes compile into the NCUA Minutes Book.
          </p>
          {noMembers && (
            <p className="mt-2 text-xs text-bdu-gold" data-testid="admin-board-meetings-hint">
              Add your first member in Section 1 (Roster) to schedule or log meetings.
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => downloadPdf("/admin/board/minutes-book.pdf", "TDSPFCU_Minutes_Book.pdf")}
                  disabled={approvedCount === 0} title={approvedCount === 0 ? "Approve at least one meeting's minutes first" : ""}
                  className="btn-ghost text-xs inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="admin-board-minutes-book">
            <BookOpen size={14} /> Minutes Book PDF ({approvedCount})
          </button>
          <button type="button" onClick={() => setModal("log")} disabled={noMembers}
                  title={noMembers ? "Add at least one roster member first" : ""}
                  className="btn-ghost text-xs inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="admin-board-log-meeting">
            <NotebookPen size={14} /> Log past meeting
          </button>
          <button type="button" onClick={() => setModal("create")} disabled={noMembers}
                  title={noMembers ? "Add at least one roster member first" : ""}
                  className="btn-gold text-xs inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="admin-board-add-meeting">
            <Plus size={14} /> Schedule Meeting
          </button>
        </div>
      </div>

      <div className="flex mb-4 pl-px" data-testid="admin-board-meeting-tabs">
        <button type="button" className={tabCls("upcoming")} onClick={() => setView("upcoming")} data-testid="admin-board-meetings-tab-upcoming">
          Upcoming ({upcoming.length})
        </button>
        <button type="button" className={tabCls("past")} onClick={() => setView("past")} data-testid="admin-board-meetings-tab-past">
          Past &amp; minutes ({past.length})
        </button>
      </div>

      <div className="space-y-3" data-testid="admin-board-meeting-list">
        {loading && <div className="text-xs text-bdu-cream/50">Loading…</div>}
        {!loading && shown.length === 0 && (
          <div className="border border-bdu-gold/15 bg-bdu-surface2 p-5 text-sm text-bdu-cream/55 italic">
            {view === "upcoming" ? "No upcoming meetings yet." : "No past meetings yet — use “Log past meeting” for ones held offline."}
          </div>
        )}
        {shown.map((m) => (
          <MeetingCard key={m._id} m={m} isPast={view === "past"} resending={resending === m._id}
                       onResend={() => resend(m)} onCancel={() => cancel(m)} onMinutes={() => setModal(m._id)} />
        ))}
      </div>

      {modal === "create" && (
        <MeetingModal members={members} onClose={() => setModal(null)}
                      onSaved={() => { setModal(null); load(); }} />
      )}
      {modal === "log" && (
        <LogMeetingModal members={members} onClose={() => setModal(null)}
                         onSaved={(mt) => { setView("past"); load(); setModal(mt._id); }} />
      )}
      {modal && modal !== "create" && modal !== "log" && (
        <MinutesModal meetingId={modal} members={members} onClose={() => setModal(null)} onChanged={load} />
      )}
    </section>
  );
}

function MeetingModal({ members, onClose, onSaved }) {
  // Default starts_at = tomorrow 7pm UTC (close enough to Eastern evening)
  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setUTCHours(19, 0, 0, 0);
    return d.toISOString().slice(0, 16);
  }, []);
  const [form, setForm] = useState({
    title: "", description: "", agenda: "",
    starts_at: defaultStart, duration_minutes: 60,
    password: "", personal_note: "",
    attendee_ids: members.map((m) => m.id),
    send_invites: true,
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggle = (id) => {
    setForm((f) => ({
      ...f,
      attendee_ids: f.attendee_ids.includes(id)
        ? f.attendee_ids.filter((x) => x !== id)
        : [...f.attendee_ids, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.attendee_ids.length) { toast.error("Pick at least one attendee"); return; }
    setBusy(true);
    try {
      // Convert local datetime-local string to UTC ISO
      const starts_iso = new Date(form.starts_at).toISOString();
      const r = await api.post("/admin/board/meetings", {
        ...form,
        starts_at: starts_iso,
        duration_minutes: Number(form.duration_minutes) || 60,
      });
      const inv = r.data.invites || {};
      toast.success(`Meeting created · ${inv.sent || 0}/${inv.recipients || 0} invites sent`);
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose} testId="admin-board-meeting-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
             className="relative max-w-2xl w-full bg-bdu-bg border border-bdu-gold/40 p-6 sm:p-8">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-bdu-cream/60 hover:text-bdu-cream"><X size={18} /></button>
        <div className="label-eyebrow flex items-center gap-2"><Video size={12} className="text-bdu-gold" /> New Jitsi Meeting</div>
        <h3 className="mt-3 font-serif text-2xl text-bdu-cream">Schedule a board meeting</h3>

        <div className="mt-5 space-y-4">
          <Field label="Title *">
            <input required value={form.title} onChange={upd("title")} className={inputCls}
                    placeholder="Phase 1 Charter Review" data-testid="admin-board-meeting-title" />
          </Field>
          <Field label="Short description (shown on invite)">
            <input value={form.description} onChange={upd("description")} className={inputCls} maxLength={200} />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Starts at (your local time) *">
              <input type="datetime-local" required value={form.starts_at} onChange={upd("starts_at")} className={inputCls}
                      data-testid="admin-board-meeting-starts" />
            </Field>
            <Field label="Duration (minutes)">
              <input type="number" min={5} max={600} value={form.duration_minutes} onChange={upd("duration_minutes")} className={inputCls} />
            </Field>
          </div>
          <Field label="Passcode (optional — tell attendees to set it on entry)">
            <input value={form.password} onChange={upd("password")} className={inputCls} placeholder="e.g. TDSP-2026" />
          </Field>
          <Field label="Agenda (shown in invite email)">
            <textarea rows={4} value={form.agenda} onChange={upd("agenda")} className={`${inputCls} resize-none`}
                       placeholder="1. ..." />
          </Field>
          <Field label="Personal note (optional — appears above the Join button)">
            <textarea rows={2} value={form.personal_note} onChange={upd("personal_note")} className={`${inputCls} resize-none`} />
          </Field>

          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-bdu-cream/60 mb-2">Attendees</div>
            <div className="grid sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto border border-bdu-gold/15 p-3"
                  data-testid="admin-board-meeting-attendees">
              {members.filter(m => m.email).map((m) => (
                <label key={m.id} className="flex items-center gap-2 text-sm text-bdu-cream/80 cursor-pointer">
                  <input type="checkbox" checked={form.attendee_ids.includes(m.id)} onChange={() => toggle(m.id)} />
                  <span className="truncate">{m.name} <span className="text-bdu-cream/40">· {m.group === "board" ? "Board" : "Committee"}</span></span>
                </label>
              ))}
              {members.filter(m => m.email).length === 0 && (
                <div className="text-xs text-bdu-cream/40 italic">No members with an email on file. Add emails in Section 1.</div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn-ghost text-xs">Cancel</button>
          <button type="submit" disabled={busy} className="btn-gold text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                   data-testid="admin-board-meeting-submit">
            {busy ? "Scheduling…" : "Schedule & Send Invites"}
          </button>
        </div>
      </form>
    </Overlay>
  );
}

// ===========================================================
// Section 3 — Broadcasts
// ===========================================================

function BroadcastsSection({ members }) {
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCompose, setOpenCompose] = useState(false);
  const [openReceipts, setOpenReceipts] = useState(null);

  const load = useCallback(async () => {
    try {
      const b = await api.get("/admin/board/broadcasts");
      setBroadcasts(b.data.broadcasts || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <section data-testid="admin-board-broadcasts">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <Send size={12} className="text-bdu-gold" /> Section 3 · Broadcasts
          </div>
          <h3 className="mt-2 font-serif text-2xl text-bdu-cream">Instructions &amp; announcements</h3>
          <p className="mt-1 text-sm text-bdu-cream/55 max-w-2xl">
            Fires via Resend to the selected members. Every email carries a signed one-tap acknowledgment link — recipients just tap it to record a read receipt.
          </p>
          {members.filter(m => m.email).length === 0 && (
            <p className="mt-2 text-xs text-bdu-gold" data-testid="admin-board-broadcast-hint">
              Add a member with an email address in Section 1 (Roster) to send broadcasts.
            </p>
          )}
        </div>
        <button type="button" onClick={() => setOpenCompose(true)}
                className="btn-gold text-xs inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="admin-board-compose"
                disabled={members.filter(m => m.email).length === 0}>
          <Plus size={14} /> New Broadcast
        </button>
      </div>

      <div className="space-y-3" data-testid="admin-board-broadcast-list">
        {loading && <div className="text-xs text-bdu-cream/50">Loading…</div>}
        {!loading && broadcasts.length === 0 && (
          <div className="border border-bdu-gold/15 bg-bdu-surface2 p-5 text-sm text-bdu-cream/55 italic">
            No broadcasts sent yet.
          </div>
        )}
        {broadcasts.map((b) => (
          <div key={b._id} className="border border-bdu-gold/20 bg-bdu-surface2 p-5"
                data-testid={`admin-board-broadcast-${b._id}`}>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] tracking-[0.22em] uppercase text-bdu-gold">{formatIso(b.sent_at)}</div>
                <div className="font-serif text-lg text-bdu-cream mt-1">{b.subject}</div>
                <div className="mt-2 text-xs text-bdu-cream/55">
                  Sent to {b.recipients?.length || 0} · {b.sent_count} delivered · {b.failed_count} failed · {(b.acknowledged_by || []).length} acknowledged
                </div>
              </div>
              <button type="button" onClick={() => setOpenReceipts(b)}
                      className="text-xs tracking-[0.2em] uppercase border border-bdu-gold/40 hover:bg-bdu-gold/10 px-3 py-2 text-bdu-cream"
                      data-testid={`admin-board-broadcast-${b._id}-receipts`}>
                View receipts
              </button>
            </div>
          </div>
        ))}
      </div>

      {openCompose && (
        <BroadcastModal
          members={members}
          onClose={() => setOpenCompose(false)}
          onSaved={() => { setOpenCompose(false); load(); }}
        />
      )}
      {openReceipts && (
        <ReceiptsModal
          broadcast={openReceipts}
          onClose={() => setOpenReceipts(null)}
        />
      )}
    </section>
  );
}

function BroadcastModal({ members, onClose, onSaved }) {
  const [mode, setMode] = useState("groups"); // groups | specific
  const [groups, setGroups] = useState(["board", "committee"]);
  const [picked, setPicked] = useState([]);
  const [form, setForm] = useState({ subject: "", body: "", reply_to: "" });
  const [busy, setBusy] = useState(false);

  const toggleGroup = (g) => setGroups((prev) => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);
  const togglePerson = (id) => setPicked((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      subject: form.subject, body: form.body,
      reply_to: form.reply_to.trim() || null,
      recipient_ids: mode === "specific" ? picked : null,
      recipient_groups: mode === "groups" ? groups : null,
    };
    setBusy(true);
    try {
      const r = await api.post("/admin/board/broadcasts", payload);
      toast.success(`Sent to ${r.data.sent}/${r.data.sent + r.data.failed}`);
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const eligible = members.filter(m => m.email);

  return (
    <Overlay onClose={onClose} testId="admin-board-broadcast-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
             className="relative max-w-2xl w-full bg-bdu-bg border border-bdu-gold/40 p-6 sm:p-8">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-bdu-cream/60 hover:text-bdu-cream"><X size={18} /></button>
        <div className="label-eyebrow flex items-center gap-2"><Send size={12} className="text-bdu-gold" /> New Broadcast</div>
        <h3 className="mt-3 font-serif text-2xl text-bdu-cream">Send instructions</h3>

        <div className="mt-5 space-y-4">
          <Field label="Subject *">
            <input required value={form.subject} onChange={upd("subject")} className={inputCls}
                    data-testid="admin-board-broadcast-subject" />
          </Field>
          <Field label="Message *">
            <textarea required rows={8} value={form.body} onChange={upd("body")}
                       className={`${inputCls} resize-none`}
                       placeholder="Line breaks preserved. Keep it short and action-oriented."
                       data-testid="admin-board-broadcast-body" />
          </Field>
          <Field label="Reply-to email (optional)">
            <input type="email" value={form.reply_to} onChange={upd("reply_to")} className={inputCls} placeholder="greg@blackdollarsunited.org" />
          </Field>

          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-bdu-cream/60 mb-2">Recipients</div>
            <div className="flex gap-2 mb-3">
              <button type="button" onClick={() => setMode("groups")}
                      className={`text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 border ${mode === "groups" ? "border-bdu-gold text-bdu-gold" : "border-bdu-cream/20 text-bdu-cream/60"}`}
                      data-testid="admin-board-broadcast-mode-groups">
                By group
              </button>
              <button type="button" onClick={() => setMode("specific")}
                      className={`text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 border ${mode === "specific" ? "border-bdu-gold text-bdu-gold" : "border-bdu-cream/20 text-bdu-cream/60"}`}
                      data-testid="admin-board-broadcast-mode-specific">
                Specific people
              </button>
            </div>
            {mode === "groups" && (
              <div className="flex flex-wrap gap-3">
                {[["board", "Board of Directors"], ["committee", "Organizing Committee"]].map(([g, label]) => (
                  <label key={g} className="flex items-center gap-2 text-sm text-bdu-cream/80">
                    <input type="checkbox" checked={groups.includes(g)} onChange={() => toggleGroup(g)} />
                    {label}
                  </label>
                ))}
              </div>
            )}
            {mode === "specific" && (
              <div className="grid sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto border border-bdu-gold/15 p-3">
                {eligible.map((m) => (
                  <label key={m.id} className="flex items-center gap-2 text-sm text-bdu-cream/80 cursor-pointer">
                    <input type="checkbox" checked={picked.includes(m.id)} onChange={() => togglePerson(m.id)} />
                    <span className="truncate">{m.name}</span>
                  </label>
                ))}
                {eligible.length === 0 && <div className="text-xs text-bdu-cream/40 italic">No eligible members (missing emails).</div>}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn-ghost text-xs">Cancel</button>
          <button type="submit" disabled={busy} className="btn-gold text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                   data-testid="admin-board-broadcast-send">
            {busy ? "Sending…" : "Send"}
          </button>
        </div>
      </form>
    </Overlay>
  );
}

function ReceiptsModal({ broadcast, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/admin/board/broadcasts/${broadcast._id}/receipts`); setData(r.data); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, [broadcast._id]);

  return (
    <Overlay onClose={onClose} testId="admin-board-receipts-modal">
      <div onClick={(e) => e.stopPropagation()}
            className="relative max-w-xl w-full bg-bdu-bg border border-bdu-gold/40 p-6 sm:p-8">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-bdu-cream/60 hover:text-bdu-cream"><X size={18} /></button>
        <div className="label-eyebrow flex items-center gap-2"><Shield size={12} className="text-bdu-gold" /> Read Receipts</div>
        <h3 className="mt-3 font-serif text-xl text-bdu-cream">{broadcast.subject}</h3>
        <div className="text-xs text-bdu-cream/55 mt-1">Sent {formatIso(broadcast.sent_at)}</div>

        {!data && <div className="mt-5 text-xs text-bdu-cream/50">Loading…</div>}
        {data && (
          <div className="mt-5">
            <div className="text-sm text-bdu-cream/80 mb-3" data-testid="admin-board-receipts-summary">
              {data.ack_count} of {data.total} acknowledged
            </div>
            <div className="border border-bdu-gold/15 divide-y divide-bdu-gold/10">
              {data.receipts.map((r) => (
                <div key={r.id} className="px-3 py-2 flex items-center justify-between text-sm"
                     data-testid={`admin-board-receipt-${r.id}`}>
                  <div>
                    <div className="text-bdu-cream">{r.name}</div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-bdu-cream/40">{r.group}</div>
                  </div>
                  {r.acknowledged ? (
                    <div className="text-xs text-emerald-400 flex items-center gap-1">
                      <CheckCircle2 size={12} /> {formatIso(r.acknowledged_at)}
                    </div>
                  ) : (
                    <div className="text-xs text-bdu-cream/40">pending</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Overlay>
  );
}
